import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import PropertyCard from "@/components/listings/property-card";
import { useFavorites } from "@/hooks/use-favorites";
import { useAuth } from "@/components/auth-provider";
import { Property } from "@/types/types";
import { getPropertyById } from "@/lib/property-service";

export default function FavoritesPage() {
  const { user } = useAuth();
  const { favoriteIds, isLoading: favoritesLoading } = useFavorites();
  const [properties, setProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadFavoriteProperties() {
      if (!user || favoriteIds.length === 0) {
        setProperties([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const propertyPromises = favoriteIds.map((id) => getPropertyById(id));
        const loadedProperties = await Promise.all(propertyPromises);

        // Filter out null values and only show approved properties
        const validProperties = loadedProperties.filter(
          (p): p is Property => p !== null && p.approvalStatus === "approved"
        );

        setProperties(validProperties);
      } catch (error) {
        console.error("Error loading favorite properties:", error);
      } finally {
        setIsLoading(false);
      }
    }

    if (!favoritesLoading) {
      loadFavoriteProperties();
    }
  }, [favoriteIds, favoritesLoading, user]);

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="text-center max-w-md mx-auto">
          <Heart className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-3xl font-bold mb-4">Sign in to view favorites</h1>
          <p className="text-muted-foreground mb-6">
            Save properties you like and access them from any device
          </p>
          <Link to="/auth/signin?callbackUrl=/properties/favorites">
            <Button size="lg">Sign In</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading || favoritesLoading) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">My Favorites</h1>
        <p className="text-muted-foreground">
          Properties you've saved for later
        </p>
      </div>

      {/* Content */}
      {properties.length === 0 ? (
        <div className="text-center py-16">
          <Heart className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl font-semibold mb-2">No favorites yet</h2>
          <p className="text-muted-foreground mb-6">
            Start adding properties to your favorites to see them here
          </p>
          <Link to="/properties">
            <Button>Browse Properties</Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="mb-4 text-sm text-muted-foreground">
            {properties.length}{" "}
            {properties.length === 1 ? "property" : "properties"} saved
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => (
              <PropertyCard key={property.id} listing={property} view="grid" />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
